import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  X, Upload, RefreshCw, Loader2, FileText, FileImage, AlertTriangle,
  Sparkles, CheckCircle2, Stethoscope, Activity, Save, Trash2, Eye, ZoomIn, ZoomOut,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import RightDrawer from './RightDrawer';
import { formatDateTimeIST } from '../utils/date';

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_STYLES = {
  normal:   { bg: 'bg-emerald-50',  text: 'text-emerald-700',  border: 'border-emerald-200',  icon: CheckCircle2 },
  mild:     { bg: 'bg-blue-50',     text: 'text-blue-700',     border: 'border-blue-200',     icon: Activity },
  moderate: { bg: 'bg-amber-50',    text: 'text-amber-700',    border: 'border-amber-200',    icon: AlertTriangle },
  severe:   { bg: 'bg-orange-50',   text: 'text-orange-700',   border: 'border-orange-200',   icon: AlertTriangle },
  critical: { bg: 'bg-red-50',      text: 'text-red-700',      border: 'border-red-200',      icon: AlertTriangle },
};

/**
 * TestReportDrawer — impressive viewer + AI insight panel + doctor notes
 * capture. Opens against an investigation (consultation_id + investigation_name).
 * Lists existing reports + lets the user upload new ones and read AI auto-
 * analysis side-by-side with the file viewer.
 */
export default function TestReportDrawer({
  open,
  onClose,
  consultationId,
  patientId,
  investigationName,
  investigationId,
}) {
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const reloadList = useCallback(async () => {
    if (!consultationId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/test-reports/by-consultation/${consultationId}`,
        { withCredentials: true }
      );
      const list = (data?.reports || []).filter(r => !investigationName || r.investigation_name === investigationName);
      setReports(list);
      // Auto-select latest if nothing selected
      if (!selected && list.length > 0) setSelected(list[0]);
      else if (selected) {
        const fresh = list.find(r => r.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch (e) {
      toast.error('Could not load reports');
    }
    setLoading(false);
  }, [consultationId, investigationName, selected]);

  // Initial load + polling while AI is running
  useEffect(() => {
    if (!open) return;
    reloadList();
  }, [open, reloadList]);

  useEffect(() => {
    if (!open || !selected) return;
    if (selected.ai_status === 'pending' || selected.ai_status === 'running') {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await axios.get(
            `${API}/api/test-reports/${selected.id}`, { withCredentials: true });
          setSelected(data);
          if (data.ai_status === 'done' || data.ai_status === 'error') {
            clearInterval(pollRef.current);
            reloadList();
          }
        } catch (_) { /* keep polling */ }
      }, 2500);
      return () => clearInterval(pollRef.current);
    }
  }, [open, selected?.id, selected?.ai_status, reloadList]);

  // Load blob viewer for selected file
  useEffect(() => {
    let revoke = null;
    if (!selected) { setBlobUrl(null); return; }
    setZoom(1);
    (async () => {
      try {
        const r = await axios.get(`${API}/api/test-reports/${selected.id}/file`, {
          withCredentials: true, responseType: 'blob',
        });
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setBlobUrl(url);
      } catch (_) {
        setBlobUrl(null);
      }
    })();
    setNotes(selected.doctor_notes || '');
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [selected?.id]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('File too large (max 25 MB)'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('consultation_id', consultationId || '');
    fd.append('patient_id', patientId || '');
    fd.append('investigation_name', investigationName || '');
    if (investigationId) fd.append('investigation_id', investigationId);
    // Naive modality hint based on investigation name
    const n = (investigationName || '').toLowerCase();
    if (n.includes('x-ray') || n.includes('xray')) fd.append('modality', 'xray');
    else if (n.includes('mri')) fd.append('modality', 'mri');
    else if (n.includes('ct')) fd.append('modality', 'ct');
    else if (n.includes('usg') || n.includes('ultrasound')) fd.append('modality', 'us');
    else fd.append('modality', 'lab');
    try {
      const { data } = await axios.post(`${API}/api/test-reports/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${file.name} uploaded — AI analysing…`);
      setSelected(data);
      reloadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const saveNotes = async () => {
    if (!selected) return;
    setSavingNotes(true);
    try {
      await axios.patch(`${API}/api/test-reports/${selected.id}/notes`,
        { doctor_notes: notes }, { withCredentials: true });
      toast.success('Notes saved');
      reloadList();
    } catch (_) {
      toast.error('Could not save notes');
    }
    setSavingNotes(false);
  };

  const reanalyse = async () => {
    if (!selected) return;
    try {
      await axios.post(`${API}/api/test-reports/${selected.id}/reanalyse`, {}, { withCredentials: true });
      toast.info('Re-running AI analysis…');
      const { data } = await axios.get(`${API}/api/test-reports/${selected.id}`, { withCredentials: true });
      setSelected(data);
    } catch (_) {
      toast.error('Could not restart analysis');
    }
  };

  const deleteReport = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await axios.delete(`${API}/api/test-reports/${id}`, { withCredentials: true });
      if (selected?.id === id) setSelected(null);
      reloadList();
      toast.success('Deleted');
    } catch (_) { toast.error('Could not delete'); }
  };

  const sev = selected?.ai_severity || '';
  const sevStyle = SEVERITY_STYLES[sev] || null;
  const SevIcon = sevStyle?.icon || Sparkles;
  const isImage = (selected?.content_type || '').startsWith('image/');
  const isPdf = selected?.content_type === 'application/pdf';

  return (
    <RightDrawer
      open={open}
      onClose={onClose}
      title={`Test Reports — ${investigationName || 'Investigation'}`}
      width="large"
      testId="test-report-drawer"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-0 h-full">
        {/* LEFT: Reports list + upload */}
        <div className="border-r bg-zinc-50/50 flex flex-col" data-testid="reports-list">
          <div className="p-3 border-b bg-white">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid="report-file-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full bg-gradient-to-br from-violet-600 to-blue-600 text-white"
              data-testid="report-upload-btn"
            >
              {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
              Upload report
            </Button>
            <div className="text-[10px] text-zinc-500 mt-1.5 text-center">JPG · PNG · WEBP · PDF (≤ 25 MB)</div>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {loading && reports.length === 0 ? (
              <div className="text-center py-8"><Loader2 className="h-5 w-5 mx-auto animate-spin text-zinc-400" /></div>
            ) : reports.length === 0 ? (
              <div className="text-center text-xs text-zinc-400 py-8">
                <FileText className="h-6 w-6 mx-auto opacity-40 mb-1.5" />
                No reports uploaded yet.
              </div>
            ) : (
              <ul className="space-y-1.5">
                {reports.map(r => (
                  <li
                    key={r.id}
                    onClick={() => setSelected(r)}
                    className={`group flex items-start gap-2 px-2 py-2 rounded-lg cursor-pointer transition ${
                      selected?.id === r.id ? 'bg-violet-50 border border-violet-200 ring-1 ring-violet-300' : 'hover:bg-white border border-transparent'
                    }`}
                    data-testid={`report-list-item-${r.id}`}
                  >
                    <div className="h-9 w-9 rounded-lg bg-white border flex items-center justify-center flex-shrink-0">
                      {r.content_type?.startsWith('image/') ? <FileImage className="h-4 w-4 text-blue-600" /> : <FileText className="h-4 w-4 text-rose-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-semibold text-zinc-900 truncate">{r.original_filename}</div>
                      <div className="text-[10px] text-zinc-500 truncate">{r.uploaded_at ? formatDateTimeIST(r.uploaded_at) : ''}</div>
                      <AIStatusPill status={r.ai_status} severity={r.ai_severity} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* RIGHT: Viewer + AI insights + notes */}
        <div className="overflow-y-auto" data-testid="report-detail">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-center text-zinc-400 p-12">
              <div>
                <FileText className="h-10 w-10 mx-auto opacity-30 mb-2" />
                <p className="text-sm">Select a report on the left to view it.</p>
                <p className="text-xs mt-1">Or upload a new image / PDF.</p>
              </div>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {/* File header */}
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-zinc-900 truncate" style={{ fontFamily: 'Manrope' }} data-testid="report-filename">
                    {selected.original_filename}
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    {selected.uploaded_by} · {selected.uploaded_at ? formatDateTimeIST(selected.uploaded_at) : ''} · {(selected.size / 1024).toFixed(1)} KB
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {isImage && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setZoom(z => Math.max(0.4, z - 0.2))} data-testid="zoom-out"><ZoomOut className="h-3.5 w-3.5" /></Button>
                      <span className="text-[10px] text-zinc-500 px-1 font-mono">{Math.round(zoom * 100)}%</span>
                      <Button size="sm" variant="outline" onClick={() => setZoom(z => Math.min(3, z + 0.2))} data-testid="zoom-in"><ZoomIn className="h-3.5 w-3.5" /></Button>
                    </>
                  )}
                  <Button size="sm" variant="outline" onClick={reanalyse} title="Re-run AI" data-testid="reanalyse-btn">
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteReport(selected.id)} data-testid="delete-btn">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {/* Viewer */}
              <div className="rounded-xl border bg-zinc-900 overflow-hidden">
                <div className="relative bg-checkerboard overflow-auto" style={{ minHeight: '320px', maxHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {!blobUrl ? (
                    <div className="text-zinc-400 p-8 text-sm">Loading viewer…</div>
                  ) : isImage ? (
                    <div className="relative" style={{ transform: `scale(${zoom})`, transformOrigin: 'center center', transition: 'transform 0.15s ease' }}>
                      <img
                        src={blobUrl}
                        alt={selected.original_filename}
                        className="max-w-full max-h-[50vh] block"
                        data-testid="report-image-viewer"
                        onLoad={(e) => {
                          // Capture natural dims for the SVG overlay below.
                          e.target.dataset.nw = e.target.naturalWidth;
                          e.target.dataset.nh = e.target.naturalHeight;
                        }}
                      />
                      {/* AI bbox overlay — normalised (0..1) coords. */}
                      {Array.isArray(selected.ai_regions) && selected.ai_regions.length > 0 && (
                        <svg
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          preserveAspectRatio="none"
                          viewBox="0 0 100 100"
                          data-testid="ai-annotation-overlay"
                        >
                          {selected.ai_regions.map((reg, i) => {
                            const x = Math.max(0, Math.min(1, reg.x || 0)) * 100;
                            const y = Math.max(0, Math.min(1, reg.y || 0)) * 100;
                            const w = Math.max(0, Math.min(1 - (reg.x || 0), reg.w || 0)) * 100;
                            const h = Math.max(0, Math.min(1 - (reg.y || 0), reg.h || 0)) * 100;
                            const regSev = (reg.severity || sev || 'mild').toLowerCase();
                            const stroke = regSev === 'critical' || regSev === 'severe' ? '#dc2626'
                                          : regSev === 'moderate' ? '#f59e0b'
                                          : regSev === 'normal' ? '#10b981'
                                          : '#3b82f6';
                            return (
                              <g key={i}>
                                <rect
                                  x={x} y={y} width={w} height={h}
                                  fill={stroke} fillOpacity="0.08"
                                  stroke={stroke} strokeWidth="0.4"
                                  vectorEffect="non-scaling-stroke"
                                  strokeDasharray="1.5 1"
                                />
                                <text
                                  x={x + 0.5} y={Math.max(2, y - 0.5)}
                                  fontSize="2.4" fontWeight="700" fill={stroke}
                                  style={{ paintOrder: 'stroke', stroke: 'white', strokeWidth: 0.6 }}
                                >{reg.label}</text>
                              </g>
                            );
                          })}
                        </svg>
                      )}
                    </div>
                  ) : isPdf ? (
                    <embed src={blobUrl} type="application/pdf" className="w-full" style={{ height: '50vh' }} data-testid="report-pdf-viewer" />
                  ) : (
                    <a href={blobUrl} target="_blank" rel="noreferrer" className="text-blue-400 underline p-6">
                      <Eye className="h-4 w-4 inline mr-1" /> Open file
                    </a>
                  )}
                </div>
              </div>

              {/* AI Insights */}
              <div className="rounded-xl border-2 border-violet-200 bg-gradient-to-br from-violet-50 via-white to-blue-50 p-4" data-testid="ai-insights-panel">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 text-white flex items-center justify-center shadow-md">
                    <SevIcon className="h-3.5 w-3.5" />
                  </div>
                  <div className="text-sm font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>AI Report Analysis</div>
                  {selected.ai_status === 'done' && sevStyle && (
                    <span className={`ml-auto text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${sevStyle.bg} ${sevStyle.text} ${sevStyle.border}`}>
                      {sev}
                    </span>
                  )}
                  {selected.ai_status === 'pending' || selected.ai_status === 'running' ? (
                    <span className="ml-auto text-[10px] font-semibold text-violet-700 inline-flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" /> Analysing
                    </span>
                  ) : null}
                </div>

                {selected.ai_status === 'error' ? (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                    <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
                    AI analysis failed: {selected.ai_error || 'unknown error'}. Click the refresh icon to retry.
                  </div>
                ) : (selected.ai_status === 'pending' || selected.ai_status === 'running') ? (
                  <div className="text-xs text-zinc-500 italic">Examining the file… typical wait is 5–15 seconds.</div>
                ) : (
                  <div className="space-y-3">
                    {selected.ai_impression && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700 mb-1">Impression</div>
                        <p className="text-sm text-zinc-900 leading-relaxed" data-testid="ai-impression">{selected.ai_impression}</p>
                      </div>
                    )}
                    {Array.isArray(selected.ai_findings) && selected.ai_findings.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700 mb-1">Key findings</div>
                        <ul className="space-y-1" data-testid="ai-findings">
                          {selected.ai_findings.map((f, i) => (
                            <li key={i} className="text-[13px] text-zinc-800 leading-snug flex items-start gap-1.5">
                              <span className="text-violet-500 mt-0.5">•</span>{f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {Array.isArray(selected.ai_regions) && selected.ai_regions.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700 mb-1">Annotated regions on image</div>
                        <div className="flex flex-wrap gap-1" data-testid="ai-regions-list">
                          {selected.ai_regions.map((r, i) => {
                            const sevR = (r.severity || '').toLowerCase();
                            const tone = sevR === 'critical' || sevR === 'severe' ? 'bg-red-50 text-red-700 border-red-200'
                                         : sevR === 'moderate' ? 'bg-amber-50 text-amber-700 border-amber-200'
                                         : sevR === 'normal' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                         : 'bg-blue-50 text-blue-700 border-blue-200';
                            return (
                              <span key={i} className={`text-[11px] font-semibold border rounded-md px-2 py-0.5 ${tone}`} data-testid={`ai-region-${i}`}>
                                {r.label}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {selected.ai_follow_up && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700 mb-1">Suggested follow-up</div>
                        <p className="text-sm text-zinc-800 leading-relaxed" data-testid="ai-followup">{selected.ai_follow_up}</p>
                      </div>
                    )}
                    <p className="text-[10px] italic text-violet-700">
                      AI assistance — verify against the source image / report before clinical action.
                    </p>
                  </div>
                )}
              </div>

              {/* Doctor notes */}
              <div className="rounded-xl border bg-white p-4" data-testid="doctor-notes-panel">
                <div className="flex items-center gap-2 mb-2">
                  <Stethoscope className="h-3.5 w-3.5 text-zinc-600" />
                  <div className="text-sm font-bold text-zinc-900">Doctor notes</div>
                  {selected.doctor_notes_at && (
                    <span className="ml-auto text-[10px] text-zinc-400">
                      Last edited {formatDateTimeIST(selected.doctor_notes_at)} by {selected.doctor_notes_by}
                    </span>
                  )}
                </div>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Your interpretation, plan, and any caveats for this report…"
                  className="text-sm min-h-[100px]"
                  data-testid="doctor-notes-input"
                />
                <div className="flex justify-end mt-2">
                  <Button onClick={saveNotes} disabled={savingNotes} size="sm" data-testid="save-notes-btn">
                    {savingNotes ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                    Save notes
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </RightDrawer>
  );
}

function AIStatusPill({ status, severity }) {
  if (status === 'pending' || status === 'running') {
    return (
      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-violet-700 bg-violet-50 border border-violet-200 px-1.5 py-0 rounded-full mt-1">
        <Loader2 className="h-2 w-2 animate-spin" /> AI analysing
      </span>
    );
  }
  if (status === 'error') {
    return <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0 rounded-full mt-1">AI error</span>;
  }
  if (status === 'done') {
    const s = SEVERITY_STYLES[severity];
    if (s) {
      return <span className={`inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0 rounded-full border mt-1 ${s.bg} ${s.text} ${s.border}`}>{severity}</span>;
    }
    return <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0 rounded-full mt-1"><Sparkles className="h-2 w-2" /> AI ready</span>;
  }
  return null;
}
